import { useEffect, useRef, useState } from 'react';
import {
  Activity,
  Bell,
  Bot,
  Boxes,
  Building2,
  ChevronRight,
  CircleGauge,
  Command,
  Database,
  Gauge,
  KeyRound,
  LayoutDashboard,
  Menu,
  MoonStar,
  PauseOctagon,
  Search,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  UsersRound,
  Workflow,
  X,
} from 'lucide-react';
import {
  companySeed,
  deterministicEventSequence,
  InMemoryCompanyRepository,
} from '@mk1/company-seed';
import {
  getHighestBlocker,
  getPerson,
  type CompanyRepository,
  type CompanySnapshot,
} from '@mk1/domain';
import type { ActorContext } from '@mk1/protocol';
import { Panel, PanelHeader, ProvenanceBadge, StatusPill } from '@mk1/ui';
import { ApiCompanyRepository } from './api-repository';
import { CompanyFloor, CompanyFloorPanel, DepartmentRoom, EmployeeDrawer } from './company-views';
import {
  AccessControl,
  AssetRegistry,
  ApprovalInbox,
  BottleneckPanel,
  CapacityGrid,
  EventStream,
  ExportPreviewPanel,
  GateDetail,
  GenericSurface,
  KpiStrip,
  NotificationsPanel,
  OperationsOverview,
  WorkControl,
} from './control-views';

const runtimeEnvironment = (import.meta.env.VITE_MK1_ENVIRONMENT ?? 'LOCAL').toUpperCase();

type Surface =
  | 'deck'
  | 'floor'
  | 'work'
  | 'capacity'
  | 'access'
  | 'approvals'
  | 'assets'
  | 'workforce'
  | 'audit'
  | 'notifications';

const surfaces = [
  { id: 'deck', label: 'Command Deck', icon: LayoutDashboard, group: 'Observe' },
  { id: 'floor', label: 'Company Floor', icon: Building2, group: 'Observe' },
  { id: 'work', label: 'Work Control', icon: Workflow, group: 'Operate' },
  { id: 'capacity', label: 'AI Capacity', icon: CircleGauge, group: 'Operate' },
  { id: 'access', label: 'Access Control', icon: KeyRound, group: 'Control' },
  { id: 'approvals', label: 'Approval Center', icon: ShieldCheck, group: 'Control' },
  { id: 'assets', label: 'Asset Registry', icon: Boxes, group: 'Control' },
  { id: 'workforce', label: 'AiR Workforce', icon: UsersRound, group: 'System' },
  { id: 'audit', label: 'Audit & Evidence', icon: Database, group: 'System' },
  { id: 'notifications', label: 'Notifications', icon: Bell, group: 'System' },
] as const;

const pageMeta: Record<Surface, { title: string; description: string }> = {
  deck: {
    title: 'CEO Command Deck',
    description: 'Safety, work, decisions and capacity — one truthful view.',
  },
  floor: {
    title: 'Company Floor',
    description: 'Departments, people and queues derived from the current snapshot.',
  },
  work: { title: 'Work Control', description: 'Ownership, handoffs, blockers and independent QA.' },
  capacity: {
    title: 'AI Capacity Grid',
    description: 'Provider pools remain separate from accountable personas.',
  },
  access: { title: 'Access Control', description: 'A0–A6 authority and revocation boundaries.' },
  approvals: {
    title: 'Approval Center',
    description: 'Exact action, evidence, consequence and expiry.',
  },
  assets: {
    title: 'Asset & Account Registry',
    description: 'Metadata and secret references only.',
  },
  workforce: {
    title: 'AiR Workforce',
    description: 'Competency, load, training and certification.',
  },
  audit: {
    title: 'Audit & Evidence',
    description: 'Deterministic events with correlation and provenance.',
  },
  notifications: {
    title: 'Notifications',
    description: 'Signals with a source object and useful next action.',
  },
};

function Logo() {
  return (
    <div className="logo" aria-label="MK1 Command">
      <span>
        <Command size={19} />
      </span>
      <div>
        <strong>MK1</strong>
        <small>COMMAND</small>
      </div>
    </div>
  );
}

function Sidebar({
  active,
  onNavigate,
  open,
  onClose,
  actor,
  onLogout,
}: {
  active: Surface;
  onNavigate: (surface: Surface) => void;
  open: boolean;
  onClose: () => void;
  actor: ActorContext | null;
  onLogout: () => void;
}) {
  return (
    <aside className={open ? 'sidebar is-open' : 'sidebar'} aria-label="Hauptnavigation">
      <div className="sidebar__top">
        <Logo />
        <button
          className="icon-button sidebar-close"
          onClick={onClose}
          aria-label="Navigation schließen"
        >
          <X size={18} />
        </button>
      </div>
      <nav>
        {surfaces.map((item, index) => {
          const Icon = item.icon;
          const groupLabel =
            index === 0 || surfaces[index - 1]?.group !== item.group ? item.group : null;
          return (
            <div key={item.id} className="nav-item-wrap">
              {groupLabel ? <span className="nav-group">{groupLabel}</span> : null}
              <button
                className={active === item.id ? 'nav-item is-active' : 'nav-item'}
                aria-current={active === item.id ? 'page' : undefined}
                onClick={() => {
                  onNavigate(item.id);
                  onClose();
                }}
              >
                <Icon size={17} />
                <span>{item.label}</span>
                {item.id === 'approvals' ? <b>2</b> : null}
              </button>
            </div>
          );
        })}
      </nav>
      <div className="sidebar__system">
        <div className="system-orbit">
          <span />
          <i />
          <Bot size={18} />
        </div>
        <div>
          <small>Control plane</small>
          <strong>{actor ? 'PostgreSQL / local' : 'Synthetic / local'}</strong>
        </div>
        <StatusPill label={actor ? 'audited' : 'offline safe'} tone="green" />
      </div>
      <button className="operator-card" onClick={actor ? onLogout : undefined}>
        <span>MK</span>
        <div>
          <strong>{actor?.actorId === 'HUM-002' ? 'Elissa Kamp' : 'Marvin Kamp'}</strong>
          <small>
            {actor?.actorId ?? 'HUM-001'} · {actor?.role ?? 'Demo owner'}
          </small>
        </div>
        <ChevronRight size={15} />
      </button>
    </aside>
  );
}

function Topbar({
  snapshot,
  reducedMotion,
  onReducedMotion,
  onPerson,
  onPause,
  onMenu,
}: {
  snapshot: CompanySnapshot;
  reducedMotion: boolean;
  onReducedMotion: () => void;
  onPerson: (id: string) => void;
  onPause?: () => void;
  onMenu: () => void;
}) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);
  const results =
    query.trim().length > 1
      ? snapshot.people
          .filter((person) =>
            `${person.name} ${person.id} ${person.role}`
              .toLowerCase()
              .includes(query.toLowerCase()),
          )
          .slice(0, 5)
      : [];

  return (
    <header className="topbar">
      <button className="icon-button mobile-menu" onClick={onMenu} aria-label="Navigation öffnen">
        <Menu size={19} />
      </button>
      <div className="search-wrap">
        <Search size={16} />
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search people, jobs, departments…"
          aria-label="Globale Suche"
        />
        <kbd>Ctrl K</kbd>
        {results.length ? (
          <div className="search-results">
            {results.map((person) => (
              <button
                key={person.id}
                onClick={() => {
                  onPerson(person.id);
                  setQuery('');
                }}
              >
                <span>{person.name}</span>
                <small>
                  {person.id} · {person.role}
                </small>
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="topbar__actions">
        <span className="environment">
          <i /> {runtimeEnvironment}
        </span>
        <span className="connection">
          <Activity size={14} />{' '}
          {snapshot.source.mode === 'LIVE_LOCAL' ? 'database projection' : 'fixture sync'}
        </span>
        <button
          className={reducedMotion ? 'icon-button is-active' : 'icon-button'}
          onClick={onReducedMotion}
          aria-pressed={reducedMotion}
          aria-label="Reduzierte Bewegung umschalten"
        >
          <MoonStar size={17} />
        </button>
        <button
          className="notification-button"
          aria-label={`${snapshot.notifications.filter((item) => item.acknowledgedAt === null).length} Benachrichtigungen`}
        >
          <Bell size={17} />
          <b>{snapshot.notifications.filter((item) => item.acknowledgedAt === null).length}</b>
        </button>
        <button className="pause-button" onClick={onPause} disabled={!onPause}>
          <PauseOctagon size={16} />
          <span>Pause company</span>
        </button>
      </div>
    </header>
  );
}

function AlertRail({
  snapshot,
  onGate,
}: {
  snapshot: CompanySnapshot;
  onGate: (id: string) => void;
}) {
  const blocker = getHighestBlocker(snapshot);
  return (
    <section className="alert-rail" aria-label="Wichtigste Blockade">
      <span className="alert-rail__icon">
        <ShieldAlert size={18} />
      </span>
      <div>
        <small>TOP BLOCKER · {blocker?.id}</small>
        <strong>{blocker?.blockedReason}</strong>
      </div>
      <span className="next-owner">
        <small>Next owner</small>
        <b>{blocker?.nextOwnerId}</b>
      </span>
      <button
        onClick={() => snapshot.approvals[0] && onGate(snapshot.approvals[0].id)}
        disabled={snapshot.approvals.length === 0}
      >
        Open gate packet <ChevronRight size={15} />
      </button>
    </section>
  );
}

function Deck({
  snapshot,
  onPerson,
  onDepartment,
  onGate,
  eventsVisible,
  simulatorRunning,
  onSimulator,
  operationsRunning,
  onCompanyDay,
}: {
  snapshot: CompanySnapshot;
  onPerson: (id: string) => void;
  onDepartment: (id: string) => void;
  onGate: (id: string) => void;
  eventsVisible: number;
  simulatorRunning: boolean;
  onSimulator: () => void;
  operationsRunning: boolean;
  onCompanyDay?: () => Promise<void>;
}) {
  return (
    <>
      <AlertRail snapshot={snapshot} onGate={onGate} />
      <KpiStrip snapshot={snapshot} />
      <OperationsOverview snapshot={snapshot} running={operationsRunning} onRun={onCompanyDay} />
      <div className="deck-primary">
        <CompanyFloorPanel
          snapshot={snapshot}
          onDepartment={onDepartment}
          onPerson={onPerson}
          compact
        />
        <Panel className="deck-capacity">
          <PanelHeader
            eyebrow="No invented precision"
            title="AI Capacity"
            action={<Gauge size={18} />}
          />
          <CapacityGrid snapshot={snapshot} compact />
          <button className="panel-link">
            Open full capacity grid <ChevronRight size={14} />
          </button>
        </Panel>
      </div>
      <div className="deck-secondary">
        <ApprovalInbox snapshot={snapshot} onOpen={onGate} />
        <BottleneckPanel snapshot={snapshot} />
        <EventStream
          events={snapshot.events}
          visibleCount={eventsVisible}
          running={simulatorRunning}
          onToggle={onSimulator}
        />
      </div>
    </>
  );
}

function PausePreview({
  onClose,
  paused,
  onToggle,
}: {
  onClose: () => void;
  paused: boolean;
  onToggle?: (reason: string, incidentId: string) => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [incidentId, setIncidentId] = useState('INC-');
  return (
    <div
      className="overlay"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        className="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="pause-title"
      >
        <span className="confirm-dialog__icon">
          <PauseOctagon size={27} />
        </span>
        <span className="ui-eyebrow">Guarded emergency control</span>
        <h2 id="pause-title">{paused ? 'Resume company execution' : 'Pause company execution'}</h2>
        <p>
          This control stops new runner claims and external-action gates while preserving read-only
          inspection, audit writes and explicit containment.
        </p>
        <div className="truth-note">
          <ShieldCheck size={16} />
          <span>
            Consequence is immediate. Pause is reversible; incident recovery requires HUM-001, a
            recorded reason and an incident link.
          </span>
        </div>
        <label className="confirm-dialog__field">
          Recorded reason
          <textarea rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
        </label>
        {paused ? (
          <label className="confirm-dialog__field">
            Incident link
            <input value={incidentId} onChange={(event) => setIncidentId(event.target.value)} />
          </label>
        ) : null}
        <div className="confirm-dialog__actions">
          <button className="button button--ghost" onClick={onClose}>
            Close preview
          </button>
          <button
            className={paused ? 'button button--primary' : 'button button--danger'}
            disabled={
              !onToggle ||
              submitting ||
              reason.trim().length < 8 ||
              (paused && incidentId.trim().length < 5)
            }
            onClick={() => {
              if (!onToggle) return;
              setSubmitting(true);
              setError(null);
              void onToggle(reason.trim(), incidentId.trim())
                .then(onClose)
                .catch((failure: unknown) =>
                  setError(failure instanceof Error ? failure.message : 'Pause control failed.'),
                )
                .finally(() => setSubmitting(false));
            }}
          >
            {submitting
              ? 'Recording…'
              : paused
                ? 'Resume company execution'
                : 'Pause company execution'}
          </button>
        </div>
        {error ? <p className="form-error">{error}</p> : null}
      </div>
    </div>
  );
}

function StartupState({
  state,
  error,
  onLogin,
}: {
  state: 'loading' | 'auth-required' | 'error';
  error: string | null;
  onLogin: (
    personId: 'HUM-001' | 'HUM-002',
    passphrase: string,
    totpCode?: string,
  ) => Promise<void>;
}) {
  const [personId, setPersonId] = useState<'HUM-001' | 'HUM-002'>('HUM-001');
  const [passphrase, setPassphrase] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  if (state === 'loading') {
    return (
      <main className="startup-state" aria-live="polite">
        <Logo />
        <Activity className="startup-state__spinner" size={28} />
        <h1>Lokale Control Plane wird geladen</h1>
        <p>Authentifizierung und PostgreSQL-Projektion werden geprüft.</p>
      </main>
    );
  }

  if (state === 'error') {
    return (
      <main className="startup-state" role="alert">
        <Logo />
        <ShieldAlert size={30} />
        <h1>Lokale Datenquelle nicht verfügbar</h1>
        <p>{error}</p>
        <p className="startup-state__hint">
          Starte PostgreSQL, Migration/Seed und die lokale API mit <code>npm run dev:full</code>.
        </p>
      </main>
    );
  }

  return (
    <main className="startup-state startup-state--login">
      <Logo />
      <div className="startup-state__badges">
        <ProvenanceBadge value="LIVE_LOCAL" />
        <ProvenanceBadge value="SEEDED" />
      </div>
      <span className="ui-eyebrow">Local identity · no shared login</span>
      <h1>MK1 Command anmelden</h1>
      <p>Jede lokale Sitzung ist an HUM-001 oder den begrenzten HUM-002-Bereich gebunden.</p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitting(true);
          setLoginError(null);
          void onLogin(personId, passphrase, totpCode || undefined)
            .catch((loginFailure: unknown) =>
              setLoginError(
                loginFailure instanceof Error ? loginFailure.message : 'Anmeldung fehlgeschlagen.',
              ),
            )
            .finally(() => setSubmitting(false));
        }}
      >
        <label>
          Lokale Identität
          <select
            value={personId}
            onChange={(event) => setPersonId(event.target.value as typeof personId)}
          >
            <option value="HUM-001">HUM-001 · Owner</option>
            <option value="HUM-002">HUM-002 · Bounded operator</option>
          </select>
        </label>
        <label>
          Lokale Passphrase
          <input
            type="password"
            value={passphrase}
            onChange={(event) => setPassphrase(event.target.value)}
            autoComplete="current-password"
            minLength={16}
            required
          />
        </label>
        <label>
          MFA-Code <span aria-hidden="true">·</span> bei Remote-Betrieb erforderlich
          <input
            type="text"
            value={totpCode}
            onChange={(event) => setTotpCode(event.target.value.replace(/\D/gu, '').slice(0, 6))}
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
          />
        </label>
        {loginError ? <p className="startup-state__error">{loginError}</p> : null}
        <button className="button button--primary" disabled={submitting}>
          {submitting ? 'Prüfe…' : 'Lokale Sitzung starten'}
        </button>
      </form>
      <small>HttpOnly/SameSite-Sitzung · serverseitige RBAC · TOTP-fähiger Remote-Pfad</small>
    </main>
  );
}

export function App() {
  const [demoMode] = useState(
    () =>
      import.meta.env.MODE === 'test' ||
      import.meta.env.VITE_MK1_DATA_MODE === 'SYNTHETIC_DEMO' ||
      new URLSearchParams(window.location.search).get('mode') === 'demo',
  );
  const [repository] = useState<CompanyRepository>(() =>
    demoMode ? new InMemoryCompanyRepository() : new ApiCompanyRepository(),
  );
  const [snapshot, setSnapshot] = useState<CompanySnapshot | null>(demoMode ? companySeed : null);
  const [actor, setActor] = useState<ActorContext | null>(null);
  const [startupState, setStartupState] = useState<'loading' | 'auth-required' | 'ready' | 'error'>(
    demoMode ? 'ready' : 'loading',
  );
  const [startupError, setStartupError] = useState<string | null>(null);
  const [surface, setSurface] = useState<Surface>('deck');
  const [selectedPerson, setSelectedPerson] = useState<string | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [selectedGate, setSelectedGate] = useState<string | null>(null);
  const [pausePreview, setPausePreview] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [simulatorRunning, setSimulatorRunning] = useState(false);
  const [operationsRunning, setOperationsRunning] = useState(false);
  const [eventsVisible, setEventsVisible] = useState(3);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = repository.subscribe(setSnapshot);
    if (repository instanceof ApiCompanyRepository) {
      void repository
        .session()
        .then(async (authenticatedActor) => {
          if (!authenticatedActor) {
            setStartupState('auth-required');
            return;
          }
          setActor(authenticatedActor);
          setSnapshot(await repository.getSnapshot());
          setStartupState('ready');
        })
        .catch((failure: unknown) => {
          setStartupError(
            failure instanceof Error ? failure.message : 'Lokale API nicht verfügbar.',
          );
          setStartupState('error');
        });
    }
    return unsubscribe;
  }, [repository]);
  useEffect(() => {
    if (!simulatorRunning) return;
    const delay = reducedMotion ? 2400 : 1200;
    const timer = window.setInterval(() => {
      setEventsVisible((count) => {
        if (count >= deterministicEventSequence.length) {
          setSimulatorRunning(false);
          return count;
        }
        return count + 1;
      });
    }, delay);
    return () => window.clearInterval(timer);
  }, [reducedMotion, simulatorRunning]);

  const login = async (personId: 'HUM-001' | 'HUM-002', passphrase: string, totpCode?: string) => {
    if (!(repository instanceof ApiCompanyRepository)) return;
    const authenticatedActor = await repository.login(personId, passphrase, totpCode);
    setActor(authenticatedActor);
    setSnapshot(await repository.getSnapshot());
    setStartupState('ready');
  };

  if (startupState !== 'ready' || !snapshot) {
    return (
      <StartupState
        state={startupState === 'ready' ? 'loading' : startupState}
        error={startupError}
        onLogin={login}
      />
    );
  }

  const openPerson = (id: string) => {
    setSelectedDepartment(null);
    setSelectedPerson(id);
  };
  const meta = pageMeta[surface];
  const activePerson = selectedPerson ? getPerson(snapshot, selectedPerson) : null;

  return (
    <div className={reducedMotion ? 'app is-reduced-motion' : 'app'}>
      <a className="skip-link" href="#main-content">
        Zum Hauptinhalt
      </a>
      <Sidebar
        active={surface}
        onNavigate={setSurface}
        open={navOpen}
        onClose={() => setNavOpen(false)}
        actor={actor}
        onLogout={() => {
          if (repository instanceof ApiCompanyRepository) {
            void repository.logout().finally(() => {
              setActor(null);
              setSnapshot(null);
              setStartupState('auth-required');
            });
          }
        }}
      />
      {navOpen ? (
        <button
          className="nav-scrim"
          onClick={() => setNavOpen(false)}
          aria-label="Navigation schließen"
        />
      ) : null}
      <div className="app-shell">
        <Topbar
          snapshot={snapshot}
          reducedMotion={reducedMotion}
          onReducedMotion={() => setReducedMotion((value) => !value)}
          onPerson={openPerson}
          onPause={actor?.role === 'OWNER' ? () => setPausePreview(true) : undefined}
          onMenu={() => setNavOpen(true)}
        />
        <main id="main-content">
          <header className="page-heading">
            <div>
              <span className="ui-eyebrow">
                KI-FIRMA · DAY {snapshot.project.day} · {snapshot.project.gate} ·{' '}
                {snapshot.source.mode}
              </span>
              <h1>{meta.title}</h1>
              <p>{meta.description}</p>
            </div>
            <div className="page-heading__truth">
              <div className="provenance-stack">
                <ProvenanceBadge value={snapshot.source.mode} />
                <ProvenanceBadge value={snapshot.source.recordProvenance} compact />
              </div>
              <span>
                <b>
                  {snapshot.source.mode === 'LIVE_LOCAL'
                    ? 'PostgreSQL read model'
                    : 'Fixture snapshot'}
                </b>
                <small>
                  {new Date(snapshot.source.generatedAt).toLocaleString('de-DE', {
                    timeZone: snapshot.source.displayTimezone,
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}{' '}
                  · {snapshot.source.displayTimezone}
                </small>
              </span>
            </div>
          </header>
          {surface === 'deck' ? (
            <Deck
              snapshot={snapshot}
              onPerson={openPerson}
              onDepartment={setSelectedDepartment}
              onGate={setSelectedGate}
              eventsVisible={eventsVisible}
              simulatorRunning={simulatorRunning}
              onSimulator={() => {
                if (eventsVisible >= snapshot.events.length) setEventsVisible(1);
                setSimulatorRunning((value) => !value);
              }}
              operationsRunning={operationsRunning}
              onCompanyDay={
                repository instanceof ApiCompanyRepository
                  ? async () => {
                      setOperationsRunning(true);
                      try {
                        await repository.runSyntheticCompanyDay();
                      } finally {
                        setOperationsRunning(false);
                      }
                    }
                  : undefined
              }
            />
          ) : null}
          {surface === 'floor' ? (
            <Panel className="surface-panel full-floor-panel">
              <PanelHeader
                eyebrow="Spatial organization view"
                title="Eight bounded rooms"
                action={<StatusPill label="11 people" tone="cyan" />}
              />
              <CompanyFloor
                snapshot={snapshot}
                onDepartment={setSelectedDepartment}
                onPerson={openPerson}
              />
            </Panel>
          ) : null}
          {surface === 'work' ? <WorkControl snapshot={snapshot} /> : null}
          {surface === 'capacity' ? (
            <CapacityGrid
              snapshot={snapshot}
              onNodeControl={
                repository instanceof ApiCompanyRepository
                  ? (nodeId, control) =>
                      repository.controlRunnerNode(
                        nodeId,
                        control,
                        `Owner requested ${control.toLocaleLowerCase('en-US')} from Capacity Grid.`,
                      )
                  : undefined
              }
              onCapacityObservation={
                repository instanceof ApiCompanyRepository
                  ? (input) => repository.recordCapacityObservation(input)
                  : undefined
              }
            />
          ) : null}
          {surface === 'access' ? (
            <AccessControl
              snapshot={snapshot}
              onPerson={openPerson}
              operatorRole={actor?.role}
              onRevokeGrant={
                repository instanceof ApiCompanyRepository
                  ? (id, reason) => repository.revokeGrant(id, reason)
                  : undefined
              }
              onRevokeResource={
                repository instanceof ApiCompanyRepository
                  ? (id, reason) => repository.revokeResource(id, reason)
                  : undefined
              }
              onSuspendPerson={
                repository instanceof ApiCompanyRepository
                  ? (id, reason) => repository.suspendPerson(id, reason)
                  : undefined
              }
              onPauseDepartment={
                repository instanceof ApiCompanyRepository
                  ? (id, reason) => repository.pauseDepartment(id, reason)
                  : undefined
              }
            />
          ) : null}
          {surface === 'approvals' ? (
            <div className="approval-full">
              <ApprovalInbox snapshot={snapshot} onOpen={setSelectedGate} />
              <GenericSurface
                title="Decision discipline"
                eyebrow="Approval packet contract"
                description="High-impact decisions bind exact payload, evidence, consequence and expiry. Vague approvals are unavailable."
              />
            </div>
          ) : null}
          {surface === 'assets' ? <AssetRegistry snapshot={snapshot} /> : null}
          {surface === 'workforce' ? (
            <GenericSurface
              title="AiR Workforce"
              eyebrow="11 authorized people"
              description="The exact current roster, reporting lines, competency ceilings and Day-15 review boundaries are represented in the Company Floor and employee drawers."
            />
          ) : null}
          {surface === 'audit' ? (
            <div className="audit-surface">
              <EventStream
                events={snapshot.events}
                visibleCount={snapshot.events.length}
                running={simulatorRunning}
                onToggle={() => setSimulatorRunning((value) => !value)}
              />
              <ExportPreviewPanel
                onPreview={
                  repository instanceof ApiCompanyRepository
                    ? (format) => repository.exportPreview(format)
                    : undefined
                }
              />
            </div>
          ) : null}
          {surface === 'notifications' ? (
            <NotificationsPanel
              snapshot={snapshot}
              onAcknowledge={
                repository instanceof ApiCompanyRepository
                  ? (id) => repository.acknowledgeNotification(id)
                  : undefined
              }
            />
          ) : null}
        </main>
        <footer className="app-footer">
          <span>MK1 Command · Phase 5 access, gates & emergency control</span>
          <span>
            <Sparkles size={13} />{' '}
            {snapshot.source.mode === 'LIVE_LOCAL'
              ? 'transactional audit'
              : 'deterministic local demo'}
          </span>
          <span>local PostgreSQL · no provider · no VPS · no external action</span>
        </footer>
      </div>
      {selectedDepartment ? (
        <DepartmentRoom
          snapshot={snapshot}
          departmentId={selectedDepartment}
          onClose={() => setSelectedDepartment(null)}
          onPerson={openPerson}
        />
      ) : null}
      {activePerson ? (
        <EmployeeDrawer
          snapshot={snapshot}
          personId={activePerson.id}
          onClose={() => setSelectedPerson(null)}
          onRevoke={(id) => {
            void repository.revokeTool(id);
          }}
        />
      ) : null}
      {selectedGate ? (
        <GateDetail
          snapshot={snapshot}
          gateId={selectedGate}
          onClose={() => setSelectedGate(null)}
          onDecision={
            repository instanceof ApiCompanyRepository
              ? (decision, input) => repository.decideGate(selectedGate, decision, input)
              : undefined
          }
        />
      ) : null}
      {pausePreview ? (
        <PausePreview
          onClose={() => setPausePreview(false)}
          paused={snapshot.accessControl.companyPaused}
          onToggle={
            repository instanceof ApiCompanyRepository
              ? async (reason, incidentId) => {
                  if (snapshot.accessControl.companyPaused) {
                    await repository.resumeCompany(reason, incidentId);
                  } else {
                    await repository.pauseCompany(reason);
                  }
                }
              : undefined
          }
        />
      ) : null}
    </div>
  );
}
